import { useState } from 'react';
import { collection, orderBy, query } from 'firebase/firestore';
import { callable, db } from '@/lib/firebase';
import { useDocs } from '@/lib/useDocs';
import { messageForAuthError } from '@/lib/authErrors';
import { formatDate, zoneLabel } from '@/lib/format';
import type { Org } from '@/types/domain';
import { strings } from '@/strings';

const approveOrganization = callable<
  { orgId: string; status: string; reason: string },
  { orgId: string; status: string }
>('approveOrganization');

const DECISIONS = [
  { value: 'approved', label: strings.orgApprove, primary: true },
  { value: 'rejected', label: strings.orgReject, primary: false },
  { value: 'suspended', label: strings.orgSuspend, primary: false },
];

export function OrgApprovalsPage() {
  const { rows, loading, error } = useDocs<Org>(query(collection(db, 'orgs'), orderBy('createdAt', 'desc')), []);
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const pending = rows.filter((row) => row.status === 'pending');

  async function decide(orgId: string, status: string) {
    if (busy) return;
    setBusy(true);
    setProblem(null);
    try {
      await approveOrganization({ orgId, status, reason: reasons[orgId] || '' });
    } catch (err) {
      setProblem(messageForAuthError((err as Error).message));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      <h1 className="text-xl font-bold text-primary">{strings.orgApprovalsTitle}</h1>
      {loading ? <p className="text-muted">{strings.loading}</p> : null}
      {error ? <p role="alert" className="text-danger">{strings.loadFailed}</p> : null}
      {!loading && !error && pending.length === 0 ? <p className="text-muted">{strings.orgApprovalsEmpty}</p> : null}

      <ul className="flex flex-col gap-4">
        {pending.map((org) => (
          <li key={org.id} className="flex flex-col gap-3 rounded-lg border border-border-subtle bg-white p-4">
            <div className="flex flex-wrap items-baseline gap-3">
              <span className="text-lg font-semibold text-ink">{org.name}</span>
              <span className="text-sm text-muted">{zoneLabel(org.zone)}</span>
              <span className="text-sm text-muted" dir="ltr">{formatDate(org.createdAt)}</span>
            </div>
            <p className="text-ink">{org.mission}</p>
            <p className="text-sm text-muted">
              {org.contact?.name} — {org.contact?.title} — <span dir="ltr">{org.contact?.phone}</span>
            </p>
            <label className="flex flex-col gap-1 text-xs text-muted" htmlFor={`reason-${org.id}`}>
              {strings.orgDecisionReason}
              <input id={`reason-${org.id}`} value={reasons[org.id] || ''}
                onChange={(e) => setReasons((prev) => ({ ...prev, [org.id]: e.target.value }))}
                className="rounded-md border border-border-subtle px-2 py-1 text-sm text-ink" />
            </label>
            <div className="flex flex-wrap gap-2">
              {DECISIONS.map((d) => (
                <button key={d.value} type="button" disabled={busy} onClick={() => decide(org.id, d.value)}
                  className={d.primary
                    ? 'min-h-11 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50'
                    : 'min-h-11 rounded-md border border-border-subtle bg-white px-4 py-2 text-sm font-medium text-ink transition-colors hover:bg-surface disabled:cursor-not-allowed disabled:opacity-50'}>
                  {d.label}
                </button>
              ))}
            </div>
          </li>
        ))}
      </ul>
      {problem ? <p role="alert" className="text-sm text-danger">{problem}</p> : null}
    </div>
  );
}
